import { useEffect, useState } from 'react';
import { inteligenciaApi, generacionApi, type Tema } from '../lib/api';

/* ── Shared tones (matches design TONE map) ── */
const TONE: Record<string, { soft: string; ink: string; solid: string; line: string }> = {
  copper: { soft: 'var(--copper-soft)', ink: 'var(--copper-ink)', solid: 'var(--copper)', line: 'var(--copper-line)' },
  sage:   { soft: 'var(--sage-soft)',   ink: 'var(--sage-ink)',   solid: 'var(--sage)',   line: 'var(--sage-line)'   },
  amber:  { soft: 'var(--amber-soft)',  ink: 'var(--amber)',      solid: 'var(--amber)',  line: '#e7d7ab'            },
  clay:   { soft: 'var(--clay-soft)',   ink: 'var(--clay)',       solid: 'var(--clay)',   line: '#e7c9c1'            },
  info:   { soft: 'var(--info-soft)',   ink: 'var(--info)',       solid: 'var(--info)',   line: '#cbd9e2'            },
  sand:   { soft: 'var(--sand-soft)',   ink: '#7a6047',           solid: 'var(--sand)',   line: '#e3d3bd'            },
};

const CATS = [
  { id: 'ANSIEDAD',            label: 'Ansiedad',            tone: 'copper' },
  { id: 'DEPRESIÓN',           label: 'Depresión',           tone: 'info'   },
  { id: 'AUTOESTIMA',          label: 'Autoestima',          tone: 'sage'   },
  { id: 'RELACIONES',          label: 'Relaciones',          tone: 'clay'   },
  { id: 'ESTRÉS',              label: 'Estrés',              tone: 'amber'  },
  { id: 'DUELO',               label: 'Duelo',               tone: 'sand'   },
  { id: 'TRAUMA',              label: 'Trauma',              tone: 'info'   },
  { id: 'MINDFULNESS',         label: 'Mindfulness',         tone: 'sage'   },
  { id: 'LÍMITES',             label: 'Límites',             tone: 'copper' },
  { id: 'CRECIMIENTO PERSONAL', label: 'Crecimiento',        tone: 'amber'  },
];
const catById = Object.fromEntries(CATS.map(c => [c.id, c]));

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][d.getMonth()]} ${d.getFullYear()}`;
}

/* ── CatTag ── */
function CatTag({ cat, small }: { cat: string | null; small?: boolean }) {
  const c = cat ? catById[cat] : null;
  if (!c) return <span className="tag">{cat ?? '—'}</span>;
  const t = TONE[c.tone];
  return (
    <span className="tag" style={{ background: t.soft, color: t.ink, borderColor: t.line, height: small ? 21 : 23 }}>
      <span className="dot" style={{ background: t.solid }} />
      {c.label}
    </span>
  );
}

/* ── Meter ── */
function Meter({ score }: { score: number }) {
  return (
    <span className="meter">
      <span className="meter-bar"><span className="meter-fill" style={{ width: score + '%' }} /></span>
      <span className="meter-val" style={{ color: score >= 88 ? 'var(--copper-ink)' : score >= 78 ? 'var(--sage-ink)' : 'var(--ink-2)' }}>{score}</span>
    </span>
  );
}

/* ── TopicCard ── */
function TopicCard({ t, onGenerate, busy }: { t: Tema; onGenerate: (t: Tema) => void; busy: boolean }) {
  return (
    <div className="card card-pad fade-up" style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <CatTag cat={t.categoria} />
        <Meter score={t.score} />
      </div>
      <div style={{ fontFamily: 'var(--serif)', fontSize: 18, lineHeight: 1.3, letterSpacing: '-0.01em', minHeight: 70 }}>{t.tema}</div>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="tag" style={{
          background: 'var(--surface-2)', color: 'var(--ink-2)', borderColor: 'var(--border)',
        }}>
          <BoltIco size={12} /> IA automático
        </span>
      </div>
      {t.razon_viral && (
        <div className="row" style={{ gap: 8, color: 'var(--ink-2)', fontSize: 12.5, alignItems: 'flex-start' }}>
          <span style={{ color: 'var(--sage)', marginTop: 1, flexShrink: 0 }}><TrendIco size={14} /></span>
          <span style={{ lineHeight: 1.4 }}>{t.razon_viral}</span>
        </div>
      )}
      <hr className="hr" />
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="muted" style={{ fontSize: 12 }}>{fmtDate(t.fecha)}</span>
        {t.usado ? (
          <span className="chip chip-aprobado"><span className="dot" /> Contenido creado</span>
        ) : (
          <button className="btn btn-sm btn-soft" onClick={() => onGenerate(t)} disabled={busy}>
            {busy
              ? <><span className="spinner" /> Generando…</>
              : <><LayersIco size={14} /> Generar contenido</>
            }
          </button>
        )}
      </div>
    </div>
  );
}

/* ── GenTopicsModal ── */
function GenTopicsModal({ onClose, onGenerate }: {
  onClose: () => void;
  onGenerate: (cat: string, count: number) => void;
}) {
  const [cat, setCat]     = useState('auto');
  const [count, setCount] = useState(3);
  const [phase, setPhase] = useState<'form' | 'running'>('form');

  const run = () => {
    setPhase('running');
    setTimeout(() => onGenerate(cat, count), 2100);
  };

  return (
    <div className="modal" onClick={() => phase !== 'running' && onClose()}
      style={{ background: 'rgba(47,37,25,.38)', backdropFilter: 'blur(2px)' }}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div style={{ padding: '22px 24px 0' }}>
          <div className="row" style={{ gap: 11, marginBottom: 4 }}>
            <span className="stat-ico" style={{ background: 'var(--copper-soft)', color: 'var(--copper)', width: 38, height: 38 }}><SparkIco size={19} /></span>
            <div>
              <h3 style={{ margin: 0, fontSize: 18 }}>Generar temas</h3>
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>La inteligencia analiza tendencias reales en redes LATAM</p>
            </div>
            <button className="btn btn-ghost btn-icon" style={{ marginLeft: 'auto' }} onClick={onClose} disabled={phase === 'running'}>
              <XIco size={17} />
            </button>
          </div>
        </div>
        <hr className="hr" style={{ margin: '18px 0 0' }} />

        {phase === 'form' ? (
          <div style={{ padding: 24 }}>
            <div className="field-label">Categoría</div>
            <div className="row" style={{ flexWrap: 'wrap', gap: 7, marginBottom: 20 }}>
              <button onClick={() => setCat('auto')} className="tag"
                style={{ cursor: 'pointer', height: 30, padding: '0 13px', fontWeight: 600,
                  border: cat === 'auto' ? '1px solid var(--accent)' : '1px solid var(--border)',
                  background: cat === 'auto' ? 'var(--accent-soft)' : 'var(--surface-2)',
                  color: cat === 'auto' ? 'var(--accent-ink)' : 'var(--ink-2)' }}>
                <BoltIco size={13} /> Automático
              </button>
              {CATS.map(c => (
                <button key={c.id} onClick={() => setCat(c.id === cat ? 'auto' : c.id)} className="tag"
                  style={{ cursor: 'pointer', height: 30, padding: '0 13px', fontWeight: 600,
                    border: cat === c.id ? '1px solid var(--accent)' : '1px solid var(--border)',
                    background: cat === c.id ? 'var(--accent-soft)' : 'var(--surface-2)',
                    color: cat === c.id ? 'var(--accent-ink)' : 'var(--ink-2)' }}>
                  {c.label}
                </button>
              ))}
            </div>

            <div className="field-label">Cuántos temas</div>
            <div className="seg" style={{ marginBottom: 22 }}>
              {[2, 3, 5].map(n => (
                <button key={n} className={count === n ? 'on' : ''} onClick={() => setCount(n)}>{n} temas</button>
              ))}
            </div>

            <div className="row" style={{ gap: 10, background: 'var(--info-soft)', border: '1px solid #cbd9e2', borderRadius: 'var(--r-sm)', padding: '11px 13px', color: 'var(--info)' }}>
              <InfoIco size={17} />
              <span style={{ fontSize: 12.5, color: '#3c5567' }}>El análisis automático corre 2 veces por semana. La IA generará <b>{count} temas</b> optimizados para Instagram LATAM.</span>
            </div>
          </div>
        ) : (
          <div style={{ padding: '34px 24px 40px', textAlign: 'center' }}>
            <div style={{ width: 60, height: 60, margin: '0 auto 18px', display: 'grid', placeItems: 'center', color: 'var(--copper)' }}>
              <RefreshIco size={34} />
            </div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 19 }}>Analizando tendencias…</div>
            <div className="col" style={{ gap: 9, maxWidth: 320, margin: '20px auto 0', textAlign: 'left' }}>
              {['Rastreando hashtags y audios virales en LATAM', 'Cruzando con búsquedas de salud mental', 'Puntuando potencial viral por tema'].map((s, i) => (
                <div key={i} className="row" style={{ gap: 9, fontSize: 13, color: 'var(--ink-2)', animation: 'fadeUp .4s both', animationDelay: i * 0.5 + 's' }}>
                  <span style={{ color: 'var(--sage)' }}><CheckIco size={15} /></span> {s}
                </div>
              ))}
            </div>
          </div>
        )}

        {phase === 'form' && (
          <div className="drawer-foot" style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={run}>
              <SparkIco size={16} /> Generar {count} temas
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main page ── */
export default function Inteligencia() {
  const [temas, setTemas]   = useState<Tema[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [msg, setMsg]       = useState('');
  const [modal, setModal]   = useState(false);
  const [filter, setFilter] = useState<'all' | 'available' | 'used'>('all');
  const [catFilter, setCatFilter] = useState('all');
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchTemas = () => {
    setLoading(true);
    inteligenciaApi.getTemas()
      .then(r => setTemas(r.temas))
      .catch(e => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTemas(); }, []);

  const onGenerate = (t: Tema) => {
    setBusyId(t.id);
    generacionApi.generarTodo(t)
      .then(r => { setMsg(`${r.generados} piezas generadas`); setTemas(prev => prev.map(x => x.id === t.id ? { ...x, usado: 1 } : x)); })
      .catch(e => setMsg(`Error: ${e instanceof Error ? e.message : 'desconocido'}`))
      .finally(() => setBusyId(null));
  };

  const onGenTopics = (cat: string, _count: number) => {
    inteligenciaApi.generarTemas(cat !== 'auto' ? { categoria: cat } : undefined)
      .then(r => { setMsg(`${r.insertados} temas generados`); fetchTemas(); })
      .catch(e => setMsg(`Error: ${e instanceof Error ? e.message : 'desconocido'}`));
    setModal(false);
  };

  let list = temas;
  if (filter === 'available') list = list.filter(t => !t.usado);
  if (filter === 'used')      list = list.filter(t => !!t.usado);
  if (catFilter !== 'all')    list = list.filter(t => t.categoria === catFilter);
  list = [...list].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

  return (
    <div className="page page-wide">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 28, margin: 0 }}>Inteligencia</h1>
          <p className="muted" style={{ margin: '7px 0 0', fontSize: 14, maxWidth: 560 }}>
            Temas detectados automáticamente a partir de tendencias reales de salud mental en redes sociales LATAM. Puntuados por su potencial viral.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => { setModal(true); setMsg(''); }}>
          <SparkIco size={16} /> Generar temas
        </button>
      </div>

      {/* auto-banner */}
      <div className="card card-pad row" style={{ gap: 14, marginBottom: 24, background: 'linear-gradient(100deg, var(--sage-soft), var(--surface))', borderColor: 'var(--sage-line)' }}>
        <span className="stat-ico" style={{ background: '#fff', color: 'var(--sage-ink)', width: 42, height: 42, border: '1px solid var(--sage-line)' }}><BoltIco size={20} /></span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14.5 }}>Análisis automático activo</div>
          <div className="muted" style={{ fontSize: 13 }}>La inteligencia genera temas 2 veces por semana (lunes y jueves).</div>
        </div>
        <div className="row" style={{ gap: 7, color: 'var(--sage-ink)', fontWeight: 600, fontSize: 13 }}>
          <span style={{ width: 8, height: 8, borderRadius: 99, background: 'var(--sage)', boxShadow: '0 0 0 4px var(--sage-soft)' }} /> En línea
        </div>
      </div>

      {msg && (
        <div className={`alert ${msg.startsWith('Error') ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: 16 }}>{msg}</div>
      )}
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* filters */}
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 18, gap: 12, flexWrap: 'wrap' }}>
        <div className="seg">
          {([['all','Todos'],['available','Disponibles'],['used','Producidos']] as const).map(([k,l]) => (
            <button key={k} className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
        <div className="row" style={{ gap: 7, flexWrap: 'wrap' }}>
          <button onClick={() => setCatFilter('all')} className="tag"
            style={{ cursor: 'pointer', height: 30,
              border: catFilter === 'all' ? '1px solid var(--accent)' : '1px solid var(--border)',
              background: catFilter === 'all' ? 'var(--accent-soft)' : 'var(--surface-2)',
              color: catFilter === 'all' ? 'var(--accent-ink)' : 'var(--ink-2)' }}>
            Todas
          </button>
          {CATS.map(c => (
            <button key={c.id} onClick={() => setCatFilter(c.id === catFilter ? 'all' : c.id)} className="tag"
              style={{ cursor: 'pointer', height: 30,
                border: catFilter === c.id ? '1px solid var(--accent)' : '1px solid var(--border)',
                background: catFilter === c.id ? 'var(--accent-soft)' : 'var(--surface-2)',
                color: catFilter === c.id ? 'var(--accent-ink)' : 'var(--ink-2)' }}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="loading-center"><span className="spinner" /> Cargando temas…</div>}

      {!loading && list.length === 0 && (
        <div className="card" style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--muted)' }}>
          <span style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--surface-2)', display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: 'var(--ink-2)' }}><SparkIco size={24} /></span>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 17, color: 'var(--ink)' }}>Sin temas en este filtro</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Genera nuevos temas o cambia los filtros.</div>
        </div>
      )}

      {!loading && list.length > 0 && (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))' }}>
          {list.map(t => (
            <TopicCard key={t.id} t={t} onGenerate={onGenerate} busy={busyId === t.id} />
          ))}
        </div>
      )}

      {modal && (
        <GenTopicsModal onClose={() => setModal(false)} onGenerate={onGenTopics} />
      )}
    </div>
  );
}

/* ── Icons ── */
function Svg({ children, size = 18 }: { children: React.ReactNode; size?: number }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
}
function SparkIco({ size = 18 }: { size?: number })   { return <Svg size={size}><path d="M12 3l1.8 4.7L18 9.5l-4.2 1.8L12 16l-1.8-4.7L6 9.5l4.2-1.8z"/><path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/></Svg>; }
function LayersIco({ size = 18 }: { size?: number })  { return <Svg size={size}><path d="M12 3l8 4.5-8 4.5-8-4.5z"/><path d="M4 12l8 4.5L20 12"/><path d="M4 16.5L12 21l8-4.5"/></Svg>; }
function BoltIco({ size = 18 }: { size?: number })    { return <Svg size={size}><path d="M13 3 4 14h6l-1 7 9-11h-6z"/></Svg>; }
function TrendIco({ size = 18 }: { size?: number })   { return <Svg size={size}><path d="M3 16l5-5 4 4 8-8"/><path d="M14 7h6v6"/></Svg>; }
function XIco({ size = 18 }: { size?: number })       { return <Svg size={size}><path d="M6 6l12 12M18 6 6 18"/></Svg>; }
function InfoIco({ size = 18 }: { size?: number })    { return <Svg size={size}><circle cx="12" cy="12" r="8.5"/><path d="M12 11v5M12 8h.01"/></Svg>; }
function CheckIco({ size = 18 }: { size?: number })   { return <Svg size={size}><path d="M5 12.5l4.5 4.5L19 6.5"/></Svg>; }
function RefreshIco({ size = 18 }: { size?: number }) { return <Svg size={size} ><path d="M3 12a9 9 0 0 1 15.5-6.3M21 4v4h-4"/><path d="M21 12a9 9 0 0 1-15.5 6.3M3 20v-4h4"/></Svg>; }
